import type { BrowserCommentTarget, ScopedThreadRef } from "@cmmd-forge/contracts";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import { create } from "zustand";

export interface BrowserCommentDraft {
  readonly note: string;
  readonly selecting: boolean;
  readonly target: BrowserCommentTarget | null;
}

const EMPTY_DRAFT: BrowserCommentDraft = {
  note: "",
  selecting: false,
  target: null,
};

interface BrowserCommentDraftStore {
  readonly draftByThreadKey: Record<string, BrowserCommentDraft>;
  readonly setNote: (threadRef: ScopedThreadRef, note: string) => void;
  readonly setSelecting: (threadRef: ScopedThreadRef, selecting: boolean) => void;
  readonly setTarget: (threadRef: ScopedThreadRef, target: BrowserCommentTarget | null) => void;
  readonly clear: (threadRef: ScopedThreadRef) => void;
}

function threadKey(threadRef: ScopedThreadRef): string {
  return scopedThreadKey(threadRef);
}

export const useBrowserCommentDraftStore = create<BrowserCommentDraftStore>((set) => ({
  draftByThreadKey: {},
  setNote: (threadRef, note) =>
    set((state) => ({
      draftByThreadKey: {
        ...state.draftByThreadKey,
        [threadKey(threadRef)]: {
          ...(state.draftByThreadKey[threadKey(threadRef)] ?? EMPTY_DRAFT),
          note,
        },
      },
    })),
  setSelecting: (threadRef, selecting) =>
    set((state) => ({
      draftByThreadKey: {
        ...state.draftByThreadKey,
        [threadKey(threadRef)]: {
          ...(state.draftByThreadKey[threadKey(threadRef)] ?? EMPTY_DRAFT),
          selecting,
        },
      },
    })),
  setTarget: (threadRef, target) =>
    set((state) => ({
      draftByThreadKey: {
        ...state.draftByThreadKey,
        [threadKey(threadRef)]: {
          ...(state.draftByThreadKey[threadKey(threadRef)] ?? EMPTY_DRAFT),
          target,
          selecting: false,
        },
      },
    })),
  clear: (threadRef) =>
    set((state) => {
      const key = threadKey(threadRef);
      if (!(key in state.draftByThreadKey)) {
        return state;
      }
      const { [key]: _removed, ...rest } = state.draftByThreadKey;
      return { draftByThreadKey: rest };
    }),
}));

export function selectBrowserCommentDraft(
  draftByThreadKey: Record<string, BrowserCommentDraft>,
  threadRef: ScopedThreadRef,
): BrowserCommentDraft {
  return draftByThreadKey[threadKey(threadRef)] ?? EMPTY_DRAFT;
}
